import { useEffect, useMemo, type RefObject } from 'react';
import type * as mapboxgl from 'mapbox-gl';
import type { EventWithCircuit } from '../../api/eventsService';
import type { StationData } from '../../api/stationObservationsService';
import type { RadarTile } from './useDataFetching';
import { useWindArrowsLayer } from './useWindArrowsLayer';
import {
  useCircleOverlayLayer,
  useRadarBboxOverlayLayer,
  useRadiusCirclesLayer,
  useLayerOrder,
} from './useMapOverlayLayers';
import { getMapIds } from './nowcastingMapIds';

interface UseMapOverlaysParams {
  mapRef: RefObject<mapboxgl.Map | null>;
  selectedEvent: EventWithCircuit | null;
  showWindArrows: boolean;
  stations: StationData[];
  tiles: RadarTile[];
  currentIndex: number;
  styleVersion: number;
  isCircleOverlayVisible: boolean;
  showRadarBbox: boolean;
  showRadiusCircles: boolean;
  radiusCircleCount: number;
  radiusCircleDistance: number;
}

/**
 * Custom hook to manage map overlays for the Nowcasting page main map.
 * Delegates to shared overlay hooks; adds main-map-specific localStorage
 * persistence and extra layer ordering for rain-path / storm-tracking.
 */
export function useMapOverlays({
  mapRef,
  selectedEvent,
  showWindArrows,
  stations,
  tiles,
  currentIndex,
  styleVersion,
  isCircleOverlayVisible,
  showRadarBbox,
  showRadiusCircles,
  radiusCircleCount,
  radiusCircleDistance,
}: UseMapOverlaysParams): void {
  const ids = getMapIds('main');

  // Persist circle overlay toggle to localStorage (main map only)
  useEffect(() => {
    localStorage.setItem('nowcasting-circle-overlay-visible', String(isCircleOverlayVisible));
  }, [isCircleOverlayVisible]);

  // Main map has rain-path and storm-tracking layers above stations
  const mainExtraLayers = useMemo(() => [
    'rain-path-line', 'rain-path-ticks', 'rain-path-dots', 'rain-path-labels',
    'storm-track-line', 'storm-track-points', 'storm-arrow', 'storm-label',
  ], []);

  useLayerOrder({
    mapRef,
    enabled: true,
    styleVersion,
    ids,
    extraLayers: mainExtraLayers,
  });

  useWindArrowsLayer({
    mapRef,
    enabled: true,
    selectedEvent,
    showWindArrows,
    stations,
    tiles,
    currentIndex,
    styleVersion,
    ids,
  });

  useCircleOverlayLayer({
    mapRef,
    enabled: true,
    selectedEvent,
    isVisible: isCircleOverlayVisible,
    styleVersion,
    sourceId: ids.circleOverlaySource,
    layerId: ids.circleOverlayLayer,
  });

  useRadarBboxOverlayLayer({
    mapRef,
    enabled: true,
    selectedEvent,
    isVisible: showRadarBbox,
    styleVersion,
    sourceId: ids.radarBboxSource,
    layerId: ids.radarBboxLayer,
  });

  useRadiusCirclesLayer({
    mapRef,
    enabled: true,
    selectedEvent,
    isVisible: showRadiusCircles,
    circleCount: radiusCircleCount,
    circleDistance: radiusCircleDistance,
    styleVersion,
    circleSourcePrefix: ids.radiusCircleSourcePrefix,
    circleLayerPrefix: ids.radiusCircleLayerPrefix,
    labelSourcePrefix: ids.radiusLabelSourcePrefix,
    labelLayerPrefix: ids.radiusLabelLayerPrefix,
  });
}
